import { Box, Stack, Typography } from '@mui/material';
import { CommandProps, Editor, Mark, mergeAttributes, RawCommands } from '@tiptap/core';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import React, { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface Comment {
  id: string;
  title: string;
  text: string;
  type?: 'positive' | 'negative' | 'neutral';
  quotedText?: string;
  active?: boolean;
}

interface CommentManagerProps {
  editorRef: RefObject<Editor | null>;
  comments: Comment[];
  activeCommentId: string | null;
  onCommentClick: (id: string) => void;
}

// Helper function to get comment ID at a specific position
const getCommentIdAtPos = (doc: ProseMirrorNode, pos: number): string | null => {
  if (!doc) return null;

  const marksAtPos = doc.resolve(pos).marks();
  if (!marksAtPos.length) return null;

  const commentMark = marksAtPos.find(mark => mark.type.name === 'comment');
  if (!commentMark) return null;

  return commentMark.attrs.commentId;
};

// Hook 1: Get comment ID at clicked position
export const useCommentClickHandler = (editor: Editor | null, onCommentFound: (commentId: string | null) => void) => {
  return useCallback((pos: number) => {
    if (!editor) return false;

    const doc = editor.view.state.doc;
    const commentId = getCommentIdAtPos(doc, pos);

    if (commentId) {
      onCommentFound(commentId);
      return true;
    }

    return false;
  }, [editor, onCommentFound]);
};

// Hook 2: Activate a specific comment ID
export const useCommentActivation = (editorRef: RefObject<Editor | null>) => {
  return useCallback((commentId: string | null) => {
    const editor = editorRef.current;
    if (!editor) return;
    const { tr } = editor.view.state;
    const doc = editor.view.state.doc;
    doc.descendants((node, pos) => {
      const commentMarks = node.marks.filter(mark =>
        mark.type.name === 'comment'
      );
      if (commentMarks.length) {
        commentMarks.forEach(mark => {
          const newMark = mark.type.create({
            ...mark.attrs,
            commentActive: commentId === mark.attrs.commentId
          });
          tr.removeMark(pos, pos + node.nodeSize, mark);
          tr.addMark(pos, pos + node.nodeSize, newMark);
        });
      }
      return true;
    });
    editor.view.dispatch(tr);
  }, [editorRef]);
};

// Update the CommentMark extension's addProseMirrorPlugins
export const CommentMark = Mark.create({
  name: 'comment',
  // Extend the mark's schema
  addOptions() {
    return {
      HTMLAttributes: {},
      onCommentActivated: (commentId: string | null) => { },
    };
  },

  // Add commands to apply comments
  addCommands() {
    return {
      setComment: (commentId: string, commentType: 'positive' | 'negative' | 'neutral' = 'neutral') => ({ commands }: CommandProps) => {
        return commands.setMark(this.name, { commentId, commentType, commentActive: false });
      },
      removeComment: () => ({ commands }: CommandProps) => {
        return commands.unsetMark(this.name);
      },
      toggleComment: (commentId: string, commentType: 'positive' | 'negative' | 'neutral' = 'neutral') => ({ commands }: CommandProps) => {
        return commands.toggleMark(this.name, { commentId, commentType, commentActive: false });
      },
    } as Partial<RawCommands>;
  },

  // Add custom attributes to store the comment ID
  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: element => element.getAttribute('data-comment-id'),
        renderHTML: attributes => {
          if (!attributes.commentId) {
            return {};
          }

          return {
            'data-comment-id': attributes.commentId,
          };
        },
      },
      commentType: {
        default: 'neutral',
        parseHTML: element => element.getAttribute('data-comment-type'),
        renderHTML: attributes => {
          if (!attributes.commentType) {
            return {};
          }

          return {
            'data-comment-type': attributes.commentType,
          };
        },
      },
      commentActive: {
        default: false,
        parseHTML: element => element.getAttribute('data-comment-active') === 'true',
        renderHTML: attributes => {
          return {
            'data-comment-active': attributes.commentActive ? 'true' : 'false',
          };
        },
      },
    };
  },

  // Define how the mark will be rendered in HTML
  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
        getAttrs: element => {
          if (typeof element === 'string') return null;
          if (!(element instanceof HTMLElement)) return null;
          return {
            commentId: element.getAttribute('data-comment-id'),
            commentType: element.getAttribute('data-comment-type') || 'neutral',
            commentActive: element.getAttribute('data-comment-active') === 'true',
          };
        },
      },
    ];
  },

  // Define how to render the mark in HTML
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  // Handle click events on comments using the new hooks
  addProseMirrorPlugins() {
    const { onCommentActivated } = this.options;

    return [
      new Plugin({
        props: {
          handleClick(view, pos) {
            const state = view.state;
            const doc = state.doc;
            const commentId = getCommentIdAtPos(doc, pos);
            if (commentId) {
              onCommentActivated(commentId);
              // Scroll the corresponding comment in the sidebar into view
              scrollCommentIntoView(commentId);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});


// Update useComments hook to use the new activation function
export const useComments = (
  editorRef: RefObject<Editor | null>,
  highlightedTextList?: Array<{ quotedText: string, comment: string, type?: 'positive' | 'negative' | 'neutral', id: string }>
) => {
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState<string>('');
  const activateComment = useCommentActivation(editorRef);
  const appliedCommentsRef = useRef<string>('');

  // Track editor content changes
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const updateContent = () => {
      setEditorContent(editor.getText());
    };

    // Set initial content
    updateContent();

    // Listen for editor updates
    editor.on('update', updateContent);

    return () => {
      editor.off('update', updateContent);
    };
  }, [editorRef]);

  // Convert highlightedTextList to comments format, filtering out comments where quotedText is not found and sorting by text position
  const comments = useMemo(() => {
    if (!highlightedTextList || highlightedTextList.length === 0) return [];

    return highlightedTextList
      .filter(item => item.quotedText && editorContent.includes(item.quotedText))
      .map(item => ({
        id: item.id,
        title: item.type === 'positive' ? '✅' : item.type === 'neutral' ? '💭' : '⚠️',
        text: item.comment,
        type: item.type,
        quotedText: item.quotedText,
        textPosition: editorContent.indexOf(item.quotedText),
      }))
      .sort((a, b) => a.textPosition - b.textPosition);
  }, [highlightedTextList, editorContent]);

  // Handle comment click from the sidebar using the new activation hook
  const handleCommentClick = useCallback((id: string) => {
    if (id === 'clear') {
      activateComment(null);
      setActiveCommentId(null);
      return;
    }

    activateComment(id);
    setActiveCommentId(id);
    // Scroll the corresponding highlight in the editor into view
    scrollHighlightIntoView(editorRef.current, id);
  }, [activateComment, editorRef]);

  // Apply comments when highlightedTextList changes
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && highlightedTextList && highlightedTextList.length > 0) {
      // Create a key to check if these comments have already been applied
      const commentKey = JSON.stringify(highlightedTextList.map(item => ({ id: item.id, quotedText: item.quotedText })));

      // Skip if we've already applied these exact comments
      if (appliedCommentsRef.current === commentKey) {
        return;
      }

      // Store current cursor position to restore it later
      const currentSelection = editor.state.selection;
      const currentFocus = editor.isFocused;

      // For each highlighted text, set a comment on matching text
      highlightedTextList.forEach(({ id, quotedText, type }) => {
        if (!quotedText) return;

        const content = editor.getText();
        if (content.includes(quotedText)) {
          const from = content.indexOf(quotedText);
          const to = from + quotedText.length; // Remove the +1 that was causing issues
          editor.commands.setTextSelection({ from, to });
          editor.commands.setMark('comment', { commentId: id, commentType: type, commentActive: false });
        }
      });

      // Restore original cursor position instead of resetting to 0
      if (currentFocus) {
        editor.commands.setTextSelection(currentSelection);
      } else {
        // Only clear selection if editor wasn't focused
        editor.commands.blur();
      }

      // Mark these comments as applied
      appliedCommentsRef.current = commentKey;
    }
  }, [editorRef, highlightedTextList]);

  return {
    comments,
    activeCommentId,
    handleCommentClick,
  };
};

// Helper function to scroll a comment highlight into view in the editor
const scrollHighlightIntoView = (editor: Editor | null, commentId: string) => {
  if (!editor) return;

  // Find the element with the comment ID in the editor
  const editorElement = editor.view.dom;
  const highlightElement = editorElement.querySelector(`[data-comment-id="${commentId}"]`);

  if (highlightElement) {
    highlightElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });
  }
};

// Helper function to scroll a comment in the sidebar into view
const scrollCommentIntoView = (commentId: string) => {
  // Small delay to ensure the comment is rendered/activated first
  setTimeout(() => {
    const commentElement = document.querySelector(`[data-sidebar-comment-id="${commentId}"]`);
    if (commentElement) {
      commentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, 100);
};

// The React component to manage comments in the UI
export const CommentManager: React.FC<CommentManagerProps> = ({
  editorRef,
  comments,
  activeCommentId,
  onCommentClick,
}) => {
  const activateComment = useCommentActivation(editorRef);

  if (!comments.length) return null;

  // Handle comment click using the new activation hook
  const handleCommentClick = (id: string) => {
    onCommentClick(id);
    activateComment(id);
    // Scroll the corresponding highlight in the editor into view
    scrollHighlightIntoView(editorRef.current, id);
  };

  // Helper function to get color based on comment type
  const getCommentColor = (type?: string) => {
    switch (type) {
      case 'positive':
        return {
          bg: 'rgba(76, 175, 80, 0.1)',
          bgHover: 'rgba(76, 175, 80, 0.2)',
          bgActive: 'rgba(76, 175, 80, 0.2)',
          border: 'rgba(76, 175, 80, 0.5)',
          text: 'success.main'
        };
      case 'neutral':
        return {
          bg: 'rgba(33, 150, 243, 0.1)',
          bgHover: 'rgba(33, 150, 243, 0.2)',
          bgActive: 'rgba(33, 150, 243, 0.2)',
          border: 'rgba(33, 150, 243, 0.5)',
          text: 'primary.main'
        };
      case 'negative':
      default:
        return {
          bg: 'rgba(244, 67, 54, 0.1)',
          bgHover: 'rgba(244, 67, 54, 0.2)',
          bgActive: 'rgba(244, 67, 54, 0.2)',
          border: 'rgba(244, 67, 54, 0.5)',
          text: 'error.main'
        };
    }
  };

  return (
    <Box sx={{ paddingBottom: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Feedback
        {comments.length > 0 && ` (${comments.length})`}
      </Typography>

      <Stack spacing={2} mt={1}>
        {comments.map((comment) => {
          const colors = getCommentColor(comment.type);
          const isActive = activeCommentId === comment.id || comment.active;

          return (
            <Box
              key={comment.id}
              data-sidebar-comment-id={comment.id}
              sx={{
                p: 1.5,
                borderRadius: 1,
                cursor: 'pointer',
                backgroundColor: isActive
                  ? colors.bgActive
                  : colors.bg,
                border: isActive ? '3px dashed' : '1px solid',
                borderColor: colors.border,
                '&:hover': {
                  backgroundColor: colors.bgHover,
                }
              }}
              onClick={() => handleCommentClick(comment.id)}
            >
              <Typography variant="body2">
                {comment.title} {comment.text}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}; 